import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import LevelBadge from "../../../design/components/LevelBadge/LevelBadge";
import Confetto from "../../../components/ui/Confetto";
import useSoundEffect from "../../../hooks/useSoundEffect";
import useOverlayScrollLock from "../../../hooks/useOverlayScrollLock";
import haptics from "../../../design/feedback/haptics";
import successSound from "../../../Assets/sounds/success.mp3";
import popSound from "../../../Assets/sounds/confete-pop.mp3";
import countSound from "../../../Assets/sounds/level_transition.mp3";
import Icon from "../../../design/icons/Icon";
import {
  celebrationPreviewEnabled,
  celebrationPresentation,
} from "../../../config/celebrationPreview";
import s from "./CheckinCelebration.module.scss";

// Tela 1 — GANHOS (handoff de monetização, set/2026): check → XP → barra de
// nível → pontos da competição/clubes. SÓ isso.
//   - As VitaCoins saíram daqui: ganham tela própria no fim da cadeia
//     (features/recompensas/VitaCoinsBonus), com o vídeo-bônus.
//   - O "Subiu de nível" saiu daqui: é a tela standalone
//     features/gamificacao/LevelUp, disparada pelo `levelUp` que o SERVIDOR
//     devolve. A barra abaixo só mostra o progresso dentro do nível.
const AVANCO_AUTOMATICO_MS = 3000;

export function CheckinCelebration({
  data,
  onDone,
  kind = "workout",
  preview = false,
  // Celebra SEM contar. O check, o confete e o som continuam; as fases
  // seguintes — XP e barra de nível — não entram, porque existem para contar
  // números que o servidor mandou, e há um caso em que ele ainda não mandou:
  // a atividade gravada sem rede, que entrou na fila.
  semContagem = false,
  // Uma linha sob o título. Existe para explicar a ausência dos números sem
  // que o componente precise saber por que eles faltam.
  nota = "",
}) {
  const [shown] = useState(() => celebrationPresentation(data, preview));
  const [phase, setPhase] = useState("check");
  const [xp, setXp] = useState(0);
  const [points, setPoints] = useState(0);
  const [progress, setProgress] = useState(() =>
    Math.min(
      100,
      (100 * (shown.prevCurrentLevelXp || 0)) / (shown.prevNextLevelXp || 100)
    )
  );
  const [ready, setReady] = useState(false);
  const clubes = (Array.isArray(shown.clubes) ? shown.clubes : []).filter(
    (c) => c && Number(c.pontos) > 0
  );
  const canvasRef = useRef(null);
  const doneRef = useRef(onDone);
  const finished = useRef(false);
  doneRef.current = onDone;
  const { play } = useSoundEffect();
  const title = kind === "meal" ? "Refeição registrada" : "Treino registrado";
  const nivelBarra = shown.prevLevel || shown.newLevel || 1;
  useOverlayScrollLock(true);

  useEffect(() => {
    const timers = [],
      frames = [];
    const after = (ms, fn) => timers.push(setTimeout(fn, ms));
    const frame = (fn) => frames.push(requestAnimationFrame(fn));
    const reduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    // O som de sucesso toca JUNTO com o círculo aparecendo (t=0), não meio
    // segundo depois — atrasado ele parecia reagir ao check em vez de fazê-lo.
    play(successSound, { volume: 0.75 });
    haptics.success();
    after(500, () => {
      play(popSound, { volume: 0.55 });
      if (reduced) return;
      const canvas = canvasRef.current,
        ctx = canvas?.getContext("2d");
      if (!ctx) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      let particles = Array.from(
        { length: 80 },
        () => new Confetto(canvas.width / 2, canvas.height / 2 - 110)
      );
      after(200, () =>
        particles.push(
          ...Array.from(
            { length: 45 },
            () => new Confetto(canvas.width / 2, canvas.height / 2 - 110)
          )
        )
      );
      const draw = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        particles.forEach((p) => {
          p.draw(ctx);
          p.update();
        });
        particles = particles.filter((p) => p.y < canvas.height + 60);
        if (particles.length) frame(draw);
      };
      frame(draw);
    });
    if (!semContagem) after(800, () => {
      setPhase("points");
      // Sem XP ganho (edição que não mudou nada, reenvio) não há o que
      // contar: o som de contagem sobre um "+0 XP" parecia um ganho falso.
      if ((Number(shown.xpGained) || 0) > 0) play(countSound, { volume: 0.6 });
      if (reduced) {
        setXp(shown.xpGained || 0);
        setPoints(shown.pontosGanhos || 0);
        return;
      }
      let start;
      const count = (ts) => {
        start ??= ts;
        const p = Math.min(1, (ts - start) / 1000),
          eased = 1 - Math.pow(1 - p, 3);
        setXp(Math.round((shown.xpGained || 0) * eased));
        setPoints(Math.round((shown.pontosGanhos || 0) * eased));
        if (p < 1) frame(count);
      };
      frame(count);
    });
    // Barra: do progresso anterior ao novo (1s, delay 1600). Se subiu de
    // nível, enche até o fim — a tela de LevelUp vem logo depois.
    if (!semContagem) after(1600, () =>
      setProgress(
        shown.leveledUp
          ? 100
          : Math.min(
              100,
              (100 * (shown.newCurrentLevelXp || 0)) /
                (shown.newNextLevelXp || 100)
            )
      )
    );
    if (!semContagem) after(2000, () => {
      setXp(shown.xpGained || 0);
      setPoints(shown.pontosGanhos || 0);
    });
    after(semContagem ? 1400 : 2700, () => setReady(true));
    return () => {
      timers.forEach(clearTimeout);
      frames.forEach(cancelAnimationFrame);
    };
  }, [shown, semContagem, play]);

  const continueFlow = () => {
    if (!ready || finished.current) return;
    finished.current = true;
    doneRef.current?.();
  };

  // "Avança sozinho em 3s" — depois que tudo contou.
  useEffect(() => {
    if (!ready) return undefined;
    const t = setTimeout(continueFlow, AVANCO_AUTOMATICO_MS);
    return () => clearTimeout(t);
  }, [ready]);

  const pontosCompeticao = Number(shown.pontosGanhos) > 0;
  return createPortal(
    <div
      className={s.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={continueFlow}
    >
      {shown.preview && (
        <span className={s.preview}>
          Prévia de animações · valores demonstrativos
        </span>
      )}
      <canvas ref={canvasRef} className={s.confetti} aria-hidden="true" />
      <div className={s.content} aria-live="polite">
        <div className={s.check}>
          <svg viewBox="0 0 24 24">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </div>
        <div className={s.summary}>
          <span>{title.toUpperCase()}</span>
          {nota && <p className={s.nota}>{nota}</p>}
          {phase !== "check" && <strong>+{xp} XP</strong>}
        </div>
        {phase !== "check" && (
          <div className={s.progress}>
            <div>
              <LevelBadge nivel={nivelBarra} tamanho={32} luz="none" />
              <b className={s.levelNumber}>Nível {nivelBarra}</b>
              <span>
                {shown.prevCurrentLevelXp ?? 0} / {shown.prevNextLevelXp ?? 100} XP
              </span>
            </div>
            <div className={s.track}>
              <i style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
        {phase !== "check" && (pontosCompeticao || clubes.length > 0) && (
          <div className={s.pointsCard}>
            <span className={s.trofeu} aria-hidden="true">
              <Icon name="clube.trofeu" size={18} />
            </span>
            <div>
              {/* Pontos EXATOS que o evento da competição vai somar (vêm do
                  servidor, já com bônus fixo e impulso). */}
              {pontosCompeticao && (
                <>
                  <b>+{points.toLocaleString("pt-BR")} pontos</b>
                  <p>
                    na competição
                    {shown.impulsoMult > 1
                      ? ` · impulso ${shown.impulsoMult}×`
                      : ""}
                  </p>
                </>
              )}
              {/* Clubes e desafios: quanto ESTE registro somou em cada um. */}
              {clubes.length > 0 && (
                <ul className={s.clubes}>
                  {clubes.map((c) => (
                    <li key={c.clubeId}>
                      <b>+{c.pontos} pts</b>
                      <span>
                        {c.tipo === "DESAFIO" ? "no desafio" : "no clube"}{" "}
                        {c.nome || ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        {ready && (
          <div className={s.acoes}>
            <button
              autoFocus
              type="button"
              className={s.continue}
              onClick={(e) => {
                e.stopPropagation();
                continueFlow();
              }}
            >
              Continuar
            </button>
            <small className={s.auto}>Avança sozinho em 3s</small>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function SavingOverlay() {
  useOverlayScrollLock(true);
  return createPortal(
    <div className={s.loading} role="status" aria-label="Salvando registro">
      <div className={s.dots}>
        <i />
        <i />
        <i />
      </div>
    </div>,
    document.body
  );
}

export default function CheckinPublishButton({
  onPublish,
  onDone,
  disabled,
  label = "Check-in treino",
  onBusyChange,
  kind = "workout",
  className = "",
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [data, setData] = useState(null);
  const locked = useRef(false),
    mounted = useRef(true),
    timer = useRef(null),
    preview = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(timer.current);
    };
  }, []);
  const publish = async () => {
    if (locked.current || disabled) return;
    locked.current = true;
    setBusy(true);
    onBusyChange?.(true);
    setError("");
    preview.current = celebrationPreviewEnabled();
    const started = Date.now();
    try {
      const result = await onPublish();
      if (!mounted.current) return;
      timer.current = setTimeout(() => {
        if (mounted.current) setData(result || {});
      }, Math.max(0, 3000 - (Date.now() - started)));
    } catch (e) {
      locked.current = false;
      onBusyChange?.(false);
      if (mounted.current) {
        setBusy(false);
        setError(e?.message || "");
      }
    }
  };
  return (
    <>
      {error && (
        <p role="alert" className={s.error}>
          {error}
        </p>
      )}
      <button
        type="button"
        className={`${s.publish} ${className}`}
        disabled={busy || disabled}
        onClick={publish}
      >
        {busy ? "Salvando…" : label}
      </button>
      {busy && !data && <SavingOverlay />}
      {data && (
        <CheckinCelebration
          data={data}
          kind={kind}
          preview={preview.current}
          onDone={() => {
            setData(null);
            setBusy(false);
            onBusyChange?.(false);
            locked.current = false;
            onDone?.({ ...data, preview: preview.current });
          }}
        />
      )}
    </>
  );
}
